import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import cn from 'classnames';

import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';
import { analytics, getHeroCtaDetail } from '@/utils/analytics';

import HeaderTextAnimation from '@/atoms/HeaderTextAnimation/HeaderTextAnimation';
import Icon from '@/atoms/Icon/Icon';
import { DynamicAsset } from '@/molecules/Media/index';
import Navigation from '@/organisms/Navigation/Navigation';

const HeadingTitle = ({ line, text, alignment, withAnimation }) => {
  if (isDDEnv()) {
    const headingCls =
      'text-center font-heading font-h1-mobile md:font-h2 text-h1-mobile md:text-h2 text-transparent bg-clip-text bg-quality-gold';
    return <h1 className={headingCls}>{text}</h1>;
  }

  // wh
  if (line === 1 || line === 3) {
    const headingCls = `tracking-tighter text-[68px] leading-none lg:text-[164px] lg:leading-[139px] font-heading text-center lg:${alignment}`;
    return <h1 className={headingCls}>{text}</h1>;
  }

  if (line === 2) {
    const headingCls = withAnimation
      ? 'tracking-tighter text-[68px] leading-none lg:text-[164px] inline-block lg:leading-[139px] font-heading mb-0'
      : 'tracking-tighter text-[68px] leading-none lg:text-[164px] border-b-2 md:border-b-4 border-b-white inline-block lg:leading-[139px] font-heading mb-0';
    return <h2 className={headingCls}>{text}</h2>;
  }
  return null;
};

function Header({ assets, entries, fields, slug }) {
  const {
    firstLine,
    firstLineAlignment,
    secondLine,
    secondLineAlignment,
    thirdLine,
    thirdLineAlignment,
    asset,
    animateLine,
    title,
    navigation,
    callToAction,
    subtext,
  } = fields;

  const topNavigationContent = navigation
    ? getContent(navigation.sys.id, entries)?.fields
    : null;

  const firstAlignment = `text-${firstLineAlignment?.toLowerCase()}`;
  const secondAlignment = `text-${secondLineAlignment?.toLowerCase()}`;
  const thirdAlignment = `text-${thirdLineAlignment?.toLowerCase()}`;

  const [visibleText, setVisibleText] = useState(0);

  useEffect(() => {
    if (animateLine) {
      let switchTextTimeout;
      if (secondLine && secondLine.length) {
        const switchText = () => {
          switchTextTimeout = setTimeout(() => {
            setVisibleText(
              visibleText === secondLine.length - 1 ? 0 : visibleText + 1
            );
            switchTextTimeout = setTimeout(switchText, 1700);
          }, 1700);
        };

        switchTextTimeout = setTimeout(switchText, 1700);

        return () => {
          clearTimeout(switchTextTimeout);
        };
      }
    }
  }, [animateLine, secondLine, visibleText]);

  if (callToAction) {
    callToAction.fields = getContent(callToAction.sys.id, entries).fields;
  }

  const isDD = isDDEnv();
  const isWH = isWHEnv();

  const navigationHasBackground = !!asset;
  return (
    <div
      className={`aaco-header aaco-header-wrapper mb-20 dd-kr:mb-0 md:dd-kr:mb-5 dd-en:mb-0 md:dd-en:mb-5 ${
        fields.cloudinaryAssets || asset ? 'h-screen' : ''
      }`}
      aria-label={title}
    >
      <Navigation
        assets={assets}
        hasBackground={navigationHasBackground}
        fields={topNavigationContent}
        entries={entries}
        slug={slug}
      />
      <div
        className={cn('aaco-header__image', 'absolute right-0 w-full', {
          'top-0 h-full': isDD,
          'top-[86px] h-screen ': isWH && !navigationHasBackground,
          'top-0 h-screen ': isWH && navigationHasBackground,
        })}
      >
        <DynamicAsset
          field={fields.cloudinaryAssets ?? asset}
          assets={assets}
          entries={entries}
          container="full"
          title={fields.cloudinaryAssets && title}
        />
      </div>
      <div
        className={cn(
          'aaco-header__bg-shadow absolute',
          {
            'top-0': isDD,
            'top-[86px]': isWH && !navigationHasBackground,
            'top-0': isWH && navigationHasBackground,
          },
          'right-0 bottom-0 left-0 z-10',
          'w-full',
          {
            'h-full': isDD,
            'h-screen': isWH,
          }
        )}
      />
      {/* <div className="aaco-header__bg-shadow__inset absolute right-0 bottom-0 left-0 z-10" /> */}
      <div
        className={cn(
          'aaco-header__content',
          'absolute right-0 left-0 z-20',
          {
            'bottom-[20px]': isDD,
            'bottom-0': isWH,
            'top-0': firstLine,
          },
          'flex flex-col justify-center items-center md:px-5'
        )}
      >
        <div className="container mx-auto text-white">
          {firstLine && (
            <HeadingTitle
              alignment={firstAlignment}
              line={1}
              text={firstLine}
            />
          )}

          {secondLine?.length &&
            secondLine.map((line, index) => (
              <div
                className={`${
                  visibleText !== index && animateLine && 'hidden'
                } text-center lg:${secondAlignment}`}
                key={index}
              >
                {animateLine ? (
                  <HeaderTextAnimation fullWidth={true}>
                    <HeadingTitle line={2} text={line} withAnimation />
                  </HeaderTextAnimation>
                ) : (
                  <HeadingTitle line={2} text={line} />
                )}
              </div>
            ))}
          {thirdLine && (
            <HeadingTitle
              alignment={thirdAlignment}
              line={1}
              text={thirdLine}
            />
          )}

          {subtext && !callToAction && (
            <div className="aaco-header__cta pb-5 dd-en:pt-5 dd-kr:pt-5 md:pb-10 w-full text-center text-bottom font-body text-body text-white flex flex-row justify-center font-text">
              <p className="md:w-1/3 whitespace-pre-wrap">{subtext}</p>
            </div>
          )}
        </div>
      </div>
      {callToAction && (
        <div className="aaco-header__cta absolute bottom-0 dd-en:bottom-[54px] dd-kr:bottom-[54px] dd-kr:pb-0 dd-en:pb-0 pb-5 md:pb-10 z-30 left-0 right-0 w-full text-center">
          <a
            href={callToAction.fields.link}
            className="inline-flex flex-col items-center justify-center text-white text-button font-semibold uppercase"
            onClick={(e) =>
              analytics({
                window,
                document,
                detail: getHeroCtaDetail(e, {
                  url: callToAction.fields.link,
                }),
              })
            }
          >
            <span className="text font-text font-medium">
              {callToAction.fields.name}
            </span>
            <span className="icon mt-2">
              <Icon name="chevron-down-header" width={21} height={10} />
            </span>
          </a>
        </div>
      )}
    </div>
  );
}

// @TODO: define real props for the Header based on it requirements
Header.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

Header.defaultProps = {
  assets: [],
  entries: [],
  fields: null,
};

export default Header;
